import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { memoize } from 'lodash-es';
import { map } from 'rxjs';

import { IBetslipRootState } from '../../../base/store/state';
import { SystemBankerSinglesSlipType, SystemSlipType } from '../../bet-generation/slip-type';
import BetslipPickActions from '../../picks/actions';
import { betslipPicksListCountSelector } from '../../picks/selectors';
import { BetslipTypesActions } from '../../types/actions';
import BetslipSystemBetActions from '../actions';
import { systemBetBankerSelector, systemBetSystemInfoSelector } from '../selectors';
import { SYSTEMS } from '../services/system-bet.constants';
import SystemFactoryService from '../services/system-factory.service';

@Injectable()
export default class DigitalSystemBetEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private systemFactory: SystemFactoryService,
    ) {}

    setSystemSystemType$ = createEffect(() => {
        const betslipPicksCountAndBankerSelector = createSelector(
            betslipPicksListCountSelector,
            systemBetSystemInfoSelector,
            systemBetBankerSelector,
            (picksCount, systemInfo, bankerInfo) => ({
                picksCount,
                systemInfo,
                ...bankerInfo,
            }),
        );

        return this.actions$.pipe(
            ofType(
                BetslipTypesActions.setSelectedType,
                BetslipPickActions.addPick,
                BetslipPickActions.removePick,
                BetslipPickActions.togglePick,
                BetslipPickActions.togglePickForType,
                BetslipPickActions.toggleAllPicks,
                BetslipSystemBetActions.toggleSystemBetPickBanker,
                BetslipSystemBetActions.toggleSystemBanker,
            ),
            concatLatestFrom(() => this.store.select(betslipPicksCountAndBankerSelector)),
            map(([_, state]) => {
                if (state.isBankerActive && state.hasBanker) {
                    return BetslipSystemBetActions.setSystemType({ systemType: new SystemBankerSinglesSlipType() });
                }

                const systems = this.systemFactory.getSystemsByPicksCount(state.picksCount);
                const sortedSystemSlipType = systems.sort((first, second) => this.getBetsCountFor(first) - this.getBetsCountFor(second));

                return BetslipSystemBetActions.setSystemType({ systemType: sortedSystemSlipType[0] ?? null });
            }),
        );
    });

    private getBetsCountFor = memoize((systemId: SystemSlipType) => SYSTEMS.find((a) => a.id === systemId.key.toLowerCase())?.bets ?? -1);
}
